import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Search,
  Filter,
  Eye,
  Download,
  TrendingUp,
  Package,
  Users,
  Euro,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  Send,
  Tag,
  ChevronRight,
  Trash2,
  Edit,
} from 'lucide-react';
import { useOrders } from '../../hooks/useApi';
import ApiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const OrderList = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    dateFrom: '',
    dateTo: '',
    agentId: '', // AGGIUNTO per admin
  });

  const [expandedOrder, setExpandedOrder] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalAmount: 0,
    avgOrderValue: 0,
    topCustomers: [],
  });

  // AGGIUNTO: stato per lista agenti
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const { orders, loading, pagination, loadOrders } = useOrders(filters);

  const hasDDTPDF = (order) => {
    return order.ddtPdfPath && order.status !== 'draft';
  };

  // AGGIUNTO: caricamento agenti per admin
  const loadAgents = async () => {
    if (!user?.isAdmin) return;
    
    try {
      setAgentsLoading(true);
      const response = await ApiService.request('/superadmin/agents');
      setAgents(response.agents || []);
    } catch (error) {
      console.error('Errore caricamento agenti:', error);
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  // ✅ FUNZIONE UNIFICATA PER CARICARE LE STATISTICHE
  const loadStats = async (currentFilters = {}) => {
    try {
      console.log('🔍 Caricamento statistiche con filtri:', currentFilters);
      
      // Costruisci i parametri per la chiamata API
      const statsParams = {};
      
      // Aggiungi filtri solo se hanno valore REALE
      if (currentFilters.search && currentFilters.search.trim()) {
        statsParams.search = currentFilters.search.trim();
      }
      if (currentFilters.dateFrom && currentFilters.dateFrom.trim()) {
        statsParams.dateFrom = currentFilters.dateFrom.trim();
      }
      if (currentFilters.dateTo && currentFilters.dateTo.trim()) {
        statsParams.dateTo = currentFilters.dateTo.trim();
      }
      // CORREZIONE PRINCIPALE: Controlla che agentId sia un numero valido
      if (currentFilters.agentId && currentFilters.agentId !== '' && !isNaN(currentFilters.agentId)) {
        statsParams.agentId = currentFilters.agentId;
        console.log('🎯 Filtro agente applicato:', statsParams.agentId);
      } else {
        console.log('🌐 Tutti gli agenti (nessun filtro agente)');
      }
      
      console.log('📡 Parametri API finali:', statsParams);
      
      // Chiama l'API delle statistiche
      const statsData = await ApiService.getOrderStats(statsParams);
      
      console.log('📊 Statistiche ricevute:', statsData);

      // Estrai i dati dalla struttura della risposta
      const summary = statsData.summary || {};
      const topCustomers = statsData.topCustomers || [];

      const newStats = {
        totalOrders: summary.totalOrders || 0,
        totalAmount: parseFloat(summary.totalRevenue || summary.confirmedRevenue) || 0,
        avgOrderValue: parseFloat(summary.avgOrderValue) || 0,
        topCustomers: topCustomers,
        uniqueCustomers: topCustomers.length
      };

      setStats(newStats);

      console.log('✅ Statistiche aggiornate:', {
        totalOrders: newStats.totalOrders,
        totalAmount: newStats.totalAmount,
        avgOrderValue: newStats.avgOrderValue
      });

    } catch (error) {
      console.error('❌ Errore nel caricamento statistiche:', error);
      // Imposta valori di fallback per evitare errori nell'UI
      setStats({
        totalOrders: 0,
        totalAmount: 0,
        avgOrderValue: 0,
        topCustomers: [],
        uniqueCustomers: 0
      });
    }
  };

  // Funzione per scaricare PDF
  const handleDownloadPDF = async (orderId) => {
    try {
      await ApiService.downloadOrderPDF(orderId);
    } catch (error) {
      console.error('Errore download PDF:', error);
      alert(`Errore nel download del PDF: ${error.message}`);
    }
  };

  const handleDownloadDDT = async (orderId) => {
    try {
      const result = await ApiService.downloadOrderDDT(orderId);
      if (result.success) {
        alert(`✅ DDT scaricato: ${result.fileName}`);
      }
    } catch (error) {
      ApiService.handlePDFError(error);
    }
  };

  // Funzione per visualizzare PDF
  const handleViewPDF = async (orderId) => {
    try {
      await ApiService.viewOrderPDF(orderId);
    } catch (error) {
      console.error('Errore visualizzazione PDF:', error);
      alert(`Errore nella visualizzazione del PDF: ${error.message}`);
    }
  };

  // Funzione per avviare l'eliminazione
  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setShowDeleteDialog(true);
  };

  // Funzione per confermare l'eliminazione
  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      await ApiService.deleteOrder(orderToDelete.id);
      setShowDeleteDialog(false);
      setOrderToDelete(null);
      // Ricarica la lista degli ordini E le statistiche
      loadOrders(filters);
      loadStats(filters);
    } catch (error) {
      console.error('Errore eliminazione ordine:', error);
      alert(`Errore nell'eliminazione dell'ordine: ${error.message}`);
    }
  };

  // Funzione per verificare se l'utente può eliminare un ordine
  const canDeleteOrder = (order) => {
    // Admin può eliminare tutti gli ordini
    if (user?.isAdmin) return true;
    // Agenti possono eliminare solo i propri ordini in bozza
    return order.status === 'draft' && order.agentId === user?.id;
  };

  // Funzione per continuare un ordine in bozza
  const handleContinueOrder = (order) => {
    window.location.href = `/?draft=${order.id}`;
  };

  // Funzione per verificare se l'utente può continuare un ordine
  const canContinueOrder = (order) => {
    return order.status === 'draft' && order.agentId === user?.id;
  };

  // ✅ CARICAMENTO INIZIALE (senza filtri)
  useEffect(() => {
    console.log('🚀 Caricamento iniziale statistiche');
    loadStats(); // Nessun filtro = dati globali
  }, []); // Solo al mount del componente

  // ✅ CARICAMENTO CON FILTRI (quando cambiano) - VERSIONE CORRETTA
  useEffect(() => {
    console.log('🔄 Cambio filtri, ricarico statistiche e ordini');
    console.log('📋 Filtri attuali:', filters);
    
    // Ricarica sia statistiche che ordini
    loadStats(filters);
    loadOrders(filters);
  }, [
    filters.search, 
    filters.dateFrom, 
    filters.dateTo, 
    filters.agentId, // Monitora anche i cambiamenti a stringa vuota
    filters.page,
    filters.limit
  ]);

  // AGGIUNTO: carica agenti se admin
  useEffect(() => {
    if (user?.isAdmin) {
      loadAgents();
    }
  }, [user]);

  const handleFilterChange = (field, value) => {
    console.log(`🔧 Cambio filtro ${field}:`, value);
    
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [field]: value,
        page: 1, // Reset alla prima pagina quando cambiano i filtri
      };
      
      console.log('📋 Nuovi filtri:', newFilters);
      return newFilters;
    });
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
    // Quando espandi un ordine, apri automaticamente tutte le sue categorie
    if (expandedOrder !== orderId) {
      const order = orders.find((o) => o.id === orderId);
      if (order && order.items) {
        const groupedItems = groupItemsByCategory(order.items);
        const categories = Object.keys(groupedItems);
        const newExpanded = new Set();
        categories.forEach((category) => {
          newExpanded.add(`${orderId}-${category}`);
        });
        setExpandedCategories(newExpanded);
      }
    } else {
      setExpandedCategories(new Set());
    }
  };

  const toggleCategory = (orderId, category) => {
    const key = `${orderId}-${category}`;
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCategories(newExpanded);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: {
        color: 'bg-gray-100 text-gray-800',
        icon: Clock,
        label: 'Bozza',
      },
      confirmed: {
        color: 'bg-blue-100 text-blue-800',
        icon: CheckCircle,
        label: 'Confermato',
      },
      sent: {
        color: 'bg-green-100 text-green-800',
        icon: Send,
        label: 'Inviato',
      },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Calcola il totale IVA per un ordine
  const getTotalVat = (vatTotal) => {
    return vatTotal || 0;
  };

  const resetFilters = () => {
    console.log('🔄 Reset di tutti i filtri');
    
    const defaultFilters = {
      page: 1,
      limit: 10,
      search: '',
      dateFrom: '',
      dateTo: '',
      agentId: '', // Assicurati che sia stringa vuota, non undefined
    };
    
    setFilters(defaultFilters);
    
    // FORZATURA: Ricarica statistiche immediatamente dopo il reset
    setTimeout(() => {
      console.log('🔄 Forzatura ricaricamento dopo reset');
      loadStats(defaultFilters);
    }, 100);
  };

  // Raggruppa articoli per categoria
  const groupItemsByCategory = (items) => {
    return items.reduce((groups, item) => {
      const category = item.product?.category || 'ALTRI PRODOTTI';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
      return groups;
    }, {});
  };

  // Calcola totali per categoria
  const getCategoryTotals = (categoryItems) => {
    return categoryItems.reduce(
      (acc, item) => {
        acc.subtotal += item.lineTotal;
        acc.itemCount += 1;
        acc.quantity += item.quantity;
        return acc;
      },
      { subtotal: 0, itemCount: 0, quantity: 0 }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con statistiche */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-6 py-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {user?.isAdmin ? 'Gestione Ordini (Admin)' : 'I Miei Ordini'}
              </h1>
              <p className="text-gray-600">
                {user?.isAdmin ? 'Visualizza e gestisci tutti gli ordini' : 'Gestisci e monitora tutti i tuoi ordini'}
              </p>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#c48f75' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4c4849'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#c48f75'}
            >
              <Filter className="w-4 h-4" />
              <span>Filtri</span>
              {showFilters ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Statistiche rapide */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">
                    Ordini Totali
                  </p>
                  <p className="text-3xl font-bold">{stats.totalOrders || 0}</p>
                </div>
                <Package className="w-10 h-10 text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">
                    Valore Totale
                  </p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(stats.totalAmount || 0)}
                  </p>
                </div>
                <Euro className="w-10 h-10 text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">
                    Valore Medio
                  </p>
                  <p className="text-3xl font-bold">
                    {formatCurrency(stats.avgOrderValue || 0)}
                  </p>
                </div>
                <TrendingUp className="w-10 h-10 text-purple-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">
                    Clienti Attivi
                  </p>
                  <p className="text-3xl font-bold">
                    {stats.uniqueCustomers || 0}
                  </p>
                </div>
                <Users className="w-10 h-10 text-orange-200" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6">
        {/* Pannello Filtri */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cerca ordine
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Numero ordine o cliente..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 login-input"
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange('search', e.target.value)
                    }
                  />
                </div>
              </div>

              {/* AGGIUNTO: Filtro Agente per admin */}
              {user?.isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agente
                  </label>
                  <select
                    value={filters.agentId}
                    onChange={(e) => {
                      console.log('🔧 Selezione agente:', e.target.value);
                      handleFilterChange('agentId', e.target.value);
                    }}
                    disabled={agentsLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 login-input disabled:bg-gray-50"
                  >
                    <option value="">Tutti gli agenti</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name} {agent.isAdmin && '(Admin)'} {!agent.isActive && '(Inattivo)'}
                      </option>
                    ))}
                  </select>
                  {agentsLoading && (
                    <div className="text-xs text-gray-500 mt-1">
                      Caricamento agenti...
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dal
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 login-input"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    handleFilterChange('dateFrom', e.target.value)
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Al
                </label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 login-input"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Reset Filtri
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista Ordini */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                Elenco Ordini
                {pagination.total > 0 && (
                  <span className="ml-2 text-sm text-gray-500">
                    ({pagination.total}{' '}
                    {pagination.total === 1 ? 'ordine' : 'ordini'})
                  </span>
                )}
              </h2>
              <div className="text-sm text-gray-500">
                Pagina {pagination.page} di {pagination.pages}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Caricamento ordini...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nessun ordine trovato
              </h3>
              <p className="text-sm">
                {filters.search ||
                  filters.dateFrom ||
                  filters.dateTo ||
                  filters.agentId
                  ? 'Prova a modificare i filtri di ricerca'
                  : 'Non hai ancora creato nessun ordine'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {orders.map((order) => {
                const isExpanded = expandedOrder === order.id;
                const groupedItems = groupItemsByCategory(order.items || []);
                const categories = Object.keys(groupedItems).sort();

                return (
                  <div
                    key={order.id}
                    className="p-6 hover:bg-gray-50 transition-colors"
                  >
                    {/* Riga principale ordine */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => toggleOrderExpansion(order.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          )}
                        </button>

                        <div>
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="font-semibold text-gray-900">
                              {order.orderNumber}
                            </h3>
                            {getStatusBadge(order.status)}
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(order.createdAt)}
                            </span>
                            <span className="font-medium text-gray-900">
                              {order.customer.companyName}
                            </span>
                            {user?.isAdmin && order.agent && (
                              <span className="font-medium" style={{ color: '#c48f75' }}>
                                Agente: {order.agent.name}
                              </span>
                            )}
                            <span>
                              {order._count?.items || order.items?.length || 0}{' '}
                              articoli
                            </span>
                            {categories.length > 0 && (
                              <span className="flex items-center">
                                <Tag className="w-3 h-3 mr-1" />
                                {categories.length}{' '}
                                {categories.length === 1
                                  ? 'categoria'
                                  : 'categorie'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            {formatCurrency(order.totalAmount)}
                          </div>
                          <div className="text-sm text-gray-500">
                            IVA:{' '}
                            {formatCurrency(
                              getTotalVat(
                                (parseFloat(order.vat4Amount) || 0) +
                                (parseFloat(order.vat10Amount) || 0) +
                                (parseFloat(order.vat22Amount) || 0)
                              )
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {order.pdfPath ? (
                            <button
                              onClick={() =>
                                handleViewPDF(order.id, order.orderNumber)
                              }
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Visualizza PDF"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleOrderExpansion(order.id)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Visualizza dettagli"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          )}

                          {order.pdfPath && (
                            <button
                              onClick={() =>
                                handleDownloadPDF(order.id, order.orderNumber)
                              }
                              className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                              title="Scarica PDF"
                            >
                              <Download className="w-5 h-5" />
                            </button>
                          )}

                          {hasDDTPDF(order) && (
                            <button
                              onClick={() => handleDownloadDDT(order.id, order.orderNumber)}
                              className="p-2 text-orange-600 hover:text-orange-800 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Scarica DDT (senza prezzi)"
                            >
                              <Package className="w-5 h-5" />
                            </button>
                          )}

                          {/* Pulsante continua bozza (solo per utenti normali) */}
                          {canContinueOrder(order) && (
                            <button
                              onClick={() => handleContinueOrder(order)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Continua ordine"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                          )}

                          {/* Pulsante eliminazione (condizionale) */}
                          {canDeleteOrder(order) && (
                            <button
                              onClick={() => handleDeleteOrder(order)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title={user?.isAdmin ? "Elimina ordine" : "Elimina bozza"}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Dettagli espansi */}
                    {isExpanded && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <div className={`grid gap-6 ${user?.isAdmin && order.agent ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2'}`}>
                          {/* Dati cliente */}
                          <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                              <Users className="w-4 h-4 mr-2" />
                              Dati Cliente
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                              <div>
                                <strong>Ragione Sociale:</strong>{' '}
                                {order.customer.companyName}
                              </div>
                              {order.customer.legalAddress && (
                                <div>
                                  <strong>Indirizzo:</strong>{' '}
                                  {order.customer.legalAddress}
                                </div>
                              )}
                              {order.customer.city && (
                                <div>
                                  <strong>Città:</strong> {order.customer.city}
                                  {order.customer.zipCode && ` - ${order.customer.zipCode}`}
                                </div>
                              )}
                              {order.customer.vatNumber && (
                                <div>
                                  <strong>P.IVA:</strong>{' '}
                                  {order.customer.vatNumber}
                                </div>
                              )}
                              {order.customer.email && (
                                <div>
                                  <strong>Email:</strong> {order.customer.email}
                                </div>
                              )}
                              {order.destination && (
                                <div className="mt-3 pt-3 border-t border-gray-300">
                                  <strong>Destinazione di consegna:</strong>
                                  <div className="ml-2 text-gray-700">
                                    {order.destination.name}
                                    <br />
                                    {order.destination.address}
                                    {order.destination.city && (
                                      <span>, {order.destination.city}</span>
                                    )}
                                    {order.destination.postalCode && (
                                      <span> - {order.destination.postalCode}</span>
                                    )}
                                    {order.destination.province && (
                                      <span> ({order.destination.province})</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {order.notes && (
                                <div className="mt-3 pt-3 border-t border-gray-300">
                                  <strong>Note:</strong>
                                  <div className="ml-2 text-gray-700 bg-yellow-50 p-2 rounded mt-1 text-sm">
                                    {order.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Informazioni Agente (solo per admin) */}
                          {user?.isAdmin && order.agent && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                <Users className="w-4 h-4 mr-2" />
                                Agente
                              </h4>
                              <div className="rounded-lg p-4 space-y-2 text-sm" style={{ backgroundColor: '#fcfbd6' }}>
                                <div>
                                  <strong>Nome:</strong> {order.agent.name}
                                </div>
                                {order.agent.email && (
                                  <div>
                                    <strong>Email:</strong> {order.agent.email}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Riepilogo totali */}
                          <div>
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                              <Euro className="w-4 h-4 mr-2" />
                              Riepilogo Importi
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Imponibile:</span>
                                <span className="font-medium">
                                  {formatCurrency(order.subtotal)}
                                </span>
                              </div>
                              {(parseFloat(order.vat4Amount) || 0) > 0 && (
                                <div className="flex justify-between">
                                  <span>IVA 4%:</span>
                                  <span>
                                    {formatCurrency(parseFloat(order.vat4Amount) || 0)}
                                  </span>
                                </div>
                              )}
                              {(parseFloat(order.vat10Amount) || 0) > 0 && (
                                <div className="flex justify-between">
                                  <span>IVA 10%:</span>
                                  <span>
                                    {formatCurrency(parseFloat(order.vat10Amount) || 0)}
                                  </span>
                                </div>
                              )}
                              {(parseFloat(order.vat22Amount) || 0) > 0 && (
                                <div className="flex justify-between">
                                  <span>IVA 22%:</span>
                                  <span>
                                    {formatCurrency(parseFloat(order.vat22Amount) || 0)}
                                  </span>
                                </div>
                              )}
                              <div className="border-t pt-2 flex justify-between font-bold text-green-600">
                                <span>TOTALE:</span>
                                <span>{formatCurrency(order.totalAmount)}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Articoli dell'ordine raggruppati per categoria */}
                        {order.items && order.items.length > 0 && (
                          <div className="mt-6">
                            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                              <Package className="w-4 h-4 mr-2" />
                              Articoli Ordinati ({order.items.length})
                            </h4>
                            <div className="space-y-4">
                              {categories.map((category) => {
                                const categoryItems = groupedItems[category];
                                const categoryTotals =
                                  getCategoryTotals(categoryItems);
                                const categoryKey = `${order.id}-${category}`;
                                const isCategoryExpanded =
                                  expandedCategories.has(categoryKey);

                                return (
                                  <div
                                    key={category}
                                    className="border border-gray-200 rounded-lg overflow-hidden"
                                  >
                                    {/* Header categoria */}
                                    <div
                                      className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
                                      onClick={() =>
                                        toggleCategory(order.id, category)
                                      }
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                          {isCategoryExpanded ? (
                                            <ChevronDown className="w-4 h-4 text-blue-600" />
                                          ) : (
                                            <ChevronRight className="w-4 h-4 text-blue-600" />
                                          )}
                                          <Tag className="w-4 h-4 text-blue-600" />
                                          <h5 className="font-semibold text-gray-900 uppercase tracking-wide text-sm">
                                            {category}
                                          </h5>
                                        </div>
                                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                                          <span>
                                            {categoryTotals.itemCount} articoli
                                          </span>
                                          <span>
                                            {categoryTotals.quantity} pz
                                          </span>
                                          <span className="font-semibold text-green-600">
                                            {formatCurrency(
                                              categoryTotals.subtotal
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Tabella articoli categoria */}
                                    {isCategoryExpanded && (
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                          <thead className="bg-gray-50">
                                            <tr>
                                              <th className="px-4 py-2 text-left font-medium text-gray-700">
                                                Codice
                                              </th>
                                              <th className="px-4 py-2 text-left font-medium text-gray-700">
                                                Descrizione
                                              </th>
                                              <th className="px-4 py-2 text-center font-medium text-gray-700">
                                                Qtà
                                              </th>
                                              <th className="px-4 py-2 text-right font-medium text-gray-700">
                                                Prezzo
                                              </th>
                                              <th className="px-4 py-2 text-right font-medium text-gray-700">
                                                Totale
                                              </th>
                                              <th className="px-4 py-2 text-center font-medium text-gray-700">
                                                IVA
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-gray-200">
                                            {categoryItems.map((item) => (
                                              <tr
                                                key={item.id}
                                                className="hover:bg-gray-50"
                                              >
                                                <td className="px-4 py-2">
                                                  <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                                    {item.product.code}
                                                  </span>
                                                </td>
                                                <td
                                                  className="px-4 py-2 max-w-xs truncate"
                                                  title={
                                                    item.product.description
                                                  }
                                                >
                                                  {item.product.description}
                                                </td>
                                                <td className="px-4 py-2 text-center">
                                                  <span className="font-medium bg-blue-50 text-blue-800 px-2 py-1 rounded-full text-xs">
                                                    {item.quantity}
                                                  </span>
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium">
                                                  {formatCurrency(
                                                    item.unitPrice
                                                  )}
                                                </td>
                                                <td className="px-4 py-2 text-right font-bold text-green-600">
                                                  {formatCurrency(
                                                    item.lineTotal
                                                  )}
                                                </td>
                                                <td className="px-4 py-2 text-center">
                                                  <span
                                                    className={`px-2 py-1 rounded text-xs font-medium ${item.product.vatRate ===
                                                      'FOUR'
                                                      ? 'bg-yellow-100 text-yellow-800'
                                                      : item.product
                                                        .vatRate === 'TEN'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-red-100 text-red-800'
                                                      }`}
                                                  >
                                                    {item.product.vatRate ===
                                                      'FOUR'
                                                      ? '4'
                                                      : item.product.vatRate ===
                                                        'TEN'
                                                        ? '10'
                                                        : '22'}
                                                    %
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginazione */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1} -{' '}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}{' '}
                  di {pagination.total} ordini
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Precedente
                  </button>

                  {Array.from(
                    { length: Math.min(5, pagination.pages) },
                    (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm rounded-md ${page === pagination.page
                            ? 'text-white'
                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          style={page === pagination.page ? { backgroundColor: '#c48f75' } : {}}
                        >
                          {page}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Successivo
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog conferma eliminazione */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Conferma Eliminazione
            </h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare {orderToDelete?.status === 'draft' ? 'la bozza' : 'l\'ordine'} {orderToDelete?.orderNumber}?
              {orderToDelete?.status === 'draft' ? ' Questa azione non può essere annullata.' : ' Questa azione non può essere annullata.'}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setOrderToDelete(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={confirmDeleteOrder}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList;